import {metaInfo} from '@configs';
import {APPLIEDJOBS, JOBFEED, SAVEDJOBS} from '@constants';
import loadSEO from '@helpers/loadSEO';
import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import cookie from 'cookie';
import dynamic from 'next/dynamic';

import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {Box, Card, Skeleton, Typography, useMediaQuery} from '@mui/material';
import {
  getConfigByApp,
  setQueryParams,
  utilServerSideDeviceDetection,
} from '@utils';
import type {GetServerSidePropsContext} from 'next/types';

import {Chat, InjectSearchOverlay} from '@components';
import {getCookiesToDelete, setSSRCookies} from '@helpers/ssrMap';
import ProMembershipPlanPopUp from '@jobseeker/client-harness/components/PaymentHarness/ProMembershipPopup';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {findAppliedJobs} from '@jobseeker/common-core/reducers/job/thunkServices/jobfeed';
import {userService} from '@jobseeker/common-core/services';
import {AppContextState, AppState} from '@jobseeker/common-core/types';
import {handleTracking, TrackEventUBA} from '@jobseeker/common-core/utils';
import DialogWithBottomSheet from '@jobseeker/lotus/components/DialogWithBottomSheet';
import {AppliedDesktop, AppliedMobile} from '@jobseeker/feature-job/components';
import Loader from '@jobseeker/lotus/components/Loader';
import debounce from 'lodash/throttle';
import {useRouter} from 'next/router';
import {useEffect, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

type AppliedJobsProps = {
  seoConfig: object;
  isCohortEligible: boolean;
};

const InsightsModal = dynamic(
  () => import('@jobseeker/feature-job/components/InsightsModal/V2'),
  {
    loading: () => <Loader />,
    ssr: false,
  }
);
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const AppliedJobs = ({seoConfig, isCohortEligible}: AppliedJobsProps) => {
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();
  useValidateAuth(dispatch, true);
  const {proMembership}: any = useInterceptor();
  const router = useRouter();

  const {userId} = useSelector((state: AppState) => state.auth, shallowEqual);

  let {isPro} = useSelector((state: AppState) => state.auth);
  isPro = isAppIimjobs ? isPro : true;

  const isStatusChangeCohortUser = useSelector((state: AppState) => {
    const statusChange =
      state.user.profile?.proMembershipFeatures?.statusChange;
    return typeof statusChange === 'boolean' ? statusChange : undefined;
  }, shallowEqual);

  const [insightsOpen, setInsightsOpen] = useState<boolean>(false);
  const [showProMembershipPrompt, setShowProMembershipPrompt] = useState(false);
  const [showFollowUpProMembershipPrompt, setShowFollowUpProMembershipPrompt] =
    useState(false);
  const [showStatusProMembershipPrompt, setStatusProMembershipPrompt] =
    useState(false);
  const [pageMaps, setPageMaps] = useState<number[]>([]);
  const [page, setPage] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  const [concat, setConcat] = useState<boolean>(false);
  const [openConfJobDialogHirist, setOpenConfJobDialogHirist] = useState(false);

  const {query} = router;
  let parsedFilters: number[] = [];
  if (query.filters) {
    parsedFilters = query.filters
      .toString()
      .split('_')
      .map(Number)
      .filter(num => !isNaN(num)); // Ensure valid numbers
  }
  if (isAppIimjobs && !isPro && isStatusChangeCohortUser === false) {
    parsedFilters = [];
  }

  const [status, setSelectedValues] = useState<number[]>(parsedFilters);

  const isMobile =
    'mobile' == useSelector((state: AppContextState) => state.layout?.device);
  const isDesktop = useMediaQuery('(min-width:500px)');

  const metaData = getConfigByApp(metaInfo, JOBFEED, true, 'applied-jobs');

  useEffect(() => {
    const sessionUserId = sessionStorage.getItem('userId') || '-1';

    TrackEventUBA('appliedJobsPageView', 'view', {
      ref: router.query.ref || '',
      filterApplicationStatus: isAppIimjobs
        ? router.query.status
        : router.query.filters || '',
      pageName: 'Applied Jobs Page',
      sessionUsername: sessionUserId,
    });
  }, []);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true); // Set loading to true before fetching
        await dispatch(
          findAppliedJobs({
            page,
            status:
              status.length >= 8
                ? ''
                : status.map(value => value - 1).join(','),
            concat: concat,
          })
        );
      } catch (error) {
        console.error('Error fetching jobs:', error);
      } finally {
        setLoading(false); // Set loading to false after fetching
      }
    };

    fetchJobs();
  }, [page]);

  const debounceOptions = {leading: true};

  const setPageQuery = debounce(
    (page: number) => {
      const initialPage = 0;
      const pageNo = page + initialPage;
      if (pageNo <= 1) {
        setQueryParams({page: null});
      } else setQueryParams({page: pageNo});
    },
    250,
    debounceOptions
  );
  const isSearchEnabled = useSelector(
    (state: AppState) => state.app.layout.searchOverlay,
    shallowEqual
  );
  const onScrollNew = () => {
    if (pageMaps.length) {
      let pageNo = 0;
      for (let i = 0; i <= pageMaps.length; i++) {
        if (pageMaps[i] < document.documentElement.scrollTop) {
          pageNo = pageNo + 1;
        } else {
          break;
        }
      }
      setPageQuery(pageNo + 1);
    }
  };
  const onLoadMore = (event: Event, target: HTMLElement) => {
    pageMaps.push(target.scrollTop || document.documentElement.scrollTop);
    setPageMaps(pageMaps);
    setPage(page + 1);
    setConcat(true);
  };

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} paginationEnabled={true} />

        <NavLayout>
          <ResponsiveLayout
            breadcrumbElements={
              isAppIimjobs
                ? [
                    {
                      name: 'Home',
                      item: '/',
                    },
                    {
                      name: 'Applied Jobs',
                      item: ``,
                    },
                  ]
                : []
            }
            variant={VersionVariants.VERSION_TWO}
          >
            <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />

            {loading && page === 0 ? (
              <Card
                sx={{
                  boxShadow: 1,
                  borderRadius: isAppIimjobs ? 4 : 0,
                  backgroundColor: isAppIimjobs ? '#fff' : 'transparent',
                  width: '100%',

                  boxSizing: 'border-box',
                }}
              >
                {isAppIimjobs ? (
                  <Box display="flex" justifyContent="space-between">
                    {/* Applied Jobs Title */}
                    <Typography
                      component={true ? 'p' : 'h1'}
                      display="flex"
                      fontWeight="500"
                      paddingBottom={{xxs: 0}}
                      height={30}
                      mt={4.125}
                      marginLeft={2}
                      marginBottom={2}
                      sx={{
                        fontSize: 20,
                        wordWrap: 'break-word',
                        textTransform: 'capitalize',
                      }}
                    >
                      Applied Jobs
                    </Typography>
                  </Box>
                ) : (
                  <></>
                )}
                {isAppIimjobs ? (
                  [...new Array(15)].map(index => {
                    return (
                      <Box
                        display="flex"
                        flexDirection="column"
                        gap={2}
                        p={2}
                        key={`applied-list-${index}`}
                        width="100%"
                        borderRadius="8px"
                        bgcolor={isAppIimjobs ? '#f9f9f9' : 'transparent'}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Skeleton width="15%" height={20} />
                          <Skeleton width="30%" height={20} />
                          <Skeleton width="10%" height={20} />
                          <Skeleton width="10%" height={20} />
                          <Skeleton width="10%" height={20} />
                          <Skeleton width="15%" height={20} />
                          <Skeleton
                            variant="rounded"
                            width="8%"
                            height={30}
                            sx={{borderRadius: '16px'}}
                          />
                        </Box>

                        <Box display="flex" alignItems="center" gap={2}>
                          <Skeleton variant="circular" width={50} height={50} />

                          <Box flexGrow={1}>
                            <Skeleton width="60%" height={15} />
                            <Skeleton width="40%" height={15} sx={{mt: 1}} />
                            <Skeleton width="30%" height={15} sx={{mt: 1}} />
                          </Box>

                          <Skeleton
                            variant="rounded"
                            width="15%"
                            height={30}
                            sx={{borderRadius: '16px'}}
                          />
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <>
                    <Box bgcolor="#13355c" color="#fff" p={5} mb={3}>
                      <Skeleton
                        width="40%"
                        height={30}
                        sx={{bgcolor: 'rgba(255,255,255,0.3)'}}
                      />
                      <Skeleton
                        width="60%"
                        height={20}
                        sx={{mt: 1, bgcolor: 'rgba(255,255,255,0.2)'}}
                      />
                    </Box>

                    {[...new Array(10)].map((_, index) => (
                      <Box
                        key={`applied-skeleton-${index}`}
                        p={2}
                        mb={2}
                        bgcolor={isAppIimjobs ? '#f9f9f9' : 'transparent'}
                      >
                        <Skeleton width="60%" height={20} sx={{mb: 1}} />

                        <Box display="flex" alignItems="center" gap={2}>
                          <Skeleton variant="circular" width={40} height={40} />

                          <Box flexGrow={1}>
                            <Skeleton width="40%" height={15} />
                            <Skeleton width="30%" height={12} sx={{mt: 1}} />
                          </Box>

                          <Skeleton
                            variant="rounded"
                            width={90}
                            height={30}
                            sx={{borderRadius: '16px'}}
                          />
                        </Box>
                      </Box>
                    ))}
                  </>
                )}
              </Card>
            ) : isMobile || !isDesktop ? (
              <>
                <AppliedMobile
                  setShowProMembershipPrompt={setShowProMembershipPrompt}
                  setShowFollowUpProMembershipPrompt={
                    setShowFollowUpProMembershipPrompt
                  }
                  concat={concat}
                  setStatusProMembershipPrompt={setStatusProMembershipPrompt}
                  setInsightsOpen={setInsightsOpen}
                  userId={userId}
                  onInfiniteScroll={onScrollNew}
                  onLoadMore={onLoadMore}
                  setSelectedValues={setSelectedValues}
                  selectedValues={status}
                  isStatusChangeCohortUser={isStatusChangeCohortUser}
                />
              </>
            ) : (
              <AppliedDesktop
                isCohortEligible={isCohortEligible}
                setShowProMembershipPrompt={setShowProMembershipPrompt}
                setShowFollowUpProMembershipPrompt={
                  setShowFollowUpProMembershipPrompt
                }
                setStatusProMembershipPrompt={setStatusProMembershipPrompt}
                setInsightsOpen={setInsightsOpen}
                userId={userId}
                concat={concat}
                onLoadMore={onLoadMore}
                onInfiniteScroll={onScrollNew}
                setSelectedValues={setSelectedValues}
                selectedValues={status}
                isStatusChangeCohortUser={isStatusChangeCohortUser}
              ></AppliedDesktop>
            )}

            <DialogWithBottomSheet
              title={
                <Typography
                  fontSize={{xxs: '14px', md: '16px'}}
                  fontWeight={500}
                >
                  Understanding Confidential Job Postings
                </Typography>
              }
              subTitle=""
              open={openConfJobDialogHirist}
              sx={{
                margin: 'auto',
                '& .MuiDialog-paper': {
                  minWidth: {
                    md: '500px',
                  },
                  maxWidth: {
                    md: '700px',
                  },
                  width: {md: '580px'},
                },
              }}
              onClose={() => {
                setOpenConfJobDialogHirist(false);
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  flexWrap: 'wrap',
                  pb: 2,
                  pt: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="subtitle3"
                    sx={{
                      lineHeight: '16px',
                      color: '#333333',
                    }}
                  >
                    What is a confidential job?
                  </Typography>
                  <Typography
                    variant="body3"
                    letterSpacing="0.5px"
                    sx={{
                      lineHeight: '16px',
                      color: '#7E7E7E',
                    }}
                  >
                    A confidential job is one where the hiring company chooses
                    not to reveal its name publicly. This is usually done for
                    sensitive roles, leadership transitions, or strategic hires
                    that require discretion.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="subtitle3"
                    sx={{
                      lineHeight: '16px',
                      color: '#333333',
                    }}
                  >
                    Why is the company name hidden?
                  </Typography>
                  <Typography
                    variant="body3"
                    letterSpacing="0.5px"
                    sx={{
                      lineHeight: '16px',
                      color: '#7E7E7E',
                    }}
                  >
                    Companies may post jobs confidentially to avoid alerting
                    internal teams, competitors, or clients. It’s a common
                    approach for leadership changes or new roles tied to future
                    business plans. In most cases, the company name is shared
                    once your profile is shortlisted or during the interview
                    process.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="subtitle3"
                    sx={{
                      lineHeight: '16px',
                      color: '#333333',
                    }}
                  >
                    Can I trust a confidential job?
                  </Typography>
                  <Typography
                    variant="body3"
                    sx={{
                      lineHeight: '16px',
                      color: '#7E7E7E',
                    }}
                    letterSpacing="0.5px"
                  >
                    Yes — all confidential jobs on{' '}
                    {isAppIimjobs ? 'iimjobs.com' : 'hirist.tech'} are posted by
                    verified employers or recruiters. These listings are
                    manually reviewed by our team for quality and authenticity.
                    Your application is only visible to the authorized recruiter
                    or company.
                  </Typography>
                </Box>
              </Box>
            </DialogWithBottomSheet>

            {insightsOpen && (
              <InsightsModal
                handleClose={() => setInsightsOpen(false)}
                open={insightsOpen}
                variant={VersionVariants.VERSION_TWO}
                proUser={isPro}
                proMeta={proMembership?.insightsBanner}
                setOpenConfJobDialog={setOpenConfJobDialogHirist}
              />
            )}
            {showProMembershipPrompt && (
              <ProMembershipPlanPopUp
                title="Unlock Insights With "
                subtitle="Compare your application with others using valuable insights"
                open={showProMembershipPrompt}
                handleClose={() => {
                  setShowProMembershipPrompt(false);
                  handleTracking('jsCrossProPopUp', {
                    event_category: 'UpgradeToPro',
                    UserId: sessionStorage.getItem('userId') || 'NA',
                    Device: sessionStorage.getItem('device') || 'desktop',
                    Device_Type: sessionStorage.getItem('deviceType') || 'web',
                    Status: sessionStorage.getItem('status') || 'LoggedOut',
                  });
                }}
                ubaSource="Insights - Applied Jobs"
              />
            )}
            {showFollowUpProMembershipPrompt && (
              <ProMembershipPlanPopUp
                title="Instantly Follow Up With "
                subtitle="Easily follow up with recruiters on roles where you were a great fit but didn’t hear back"
                open={showFollowUpProMembershipPrompt}
                handleClose={() => {
                  setShowFollowUpProMembershipPrompt(false);
                  handleTracking('jsCrossProPopUp', {
                    event_category: 'UpgradeToPro',
                    UserId: sessionStorage.getItem('userId') || 'NA',
                    Device: sessionStorage.getItem('device') || 'desktop',
                    Device_Type: sessionStorage.getItem('deviceType') || 'web',
                    Status: sessionStorage.getItem('status') || 'LoggedOut',
                  });
                }}
                ubaSource="Followup - Applied Jobs"
              />
            )}
            {showStatusProMembershipPrompt && (
              <ProMembershipPlanPopUp
                title="Unlock Recruiter Actions With "
                subtitle="Get instant notifications when recruiters take action on your application"
                open={showStatusProMembershipPrompt}
                handleClose={() => {
                  setStatusProMembershipPrompt(false);
                  handleTracking('jsCrossProPopUp', {
                    event_category: 'UpgradeToPro',
                    UserId: sessionStorage.getItem('userId') || 'NA',
                    Device: sessionStorage.getItem('device') || 'desktop',
                    Device_Type: sessionStorage.getItem('deviceType') || 'web',
                    Status: sessionStorage.getItem('status') || 'LoggedOut',
                  });
                }}
                ubaSource="Application Status - Applied Jobs"
              />
            )}
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <Chat />
    </>
  );
};
export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const metaData = getConfigByApp(metaInfo, JOBFEED, true, 'applied-jobs');
    let isCohortEligible = false;
    const slugUrl = context?.resolvedUrl || '';

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: APPLIEDJOBS},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    let headers = {};
    const ssrHeaders = {
      'user-agent': context.req?.headers?.['user-agent'],
      'x-forwarded-for':
        context.req?.headers?.['x-forwarded-for']
          ?.toString()
          .split(',')[0]
          .trim() ||
        context.req?.socket?.remoteAddress ||
        '',
      referer: `https://www.${process.env.NEXT_PUBLIC_APP_DOMAIN}/`,
    };

    if (context.req?.headers?.cookie) {
      const cookies = cookie.parse(context.req.headers.cookie);
      console.log(
        '🚀 ~ file: index.tsx:40 ~ store=> ~ cookies:',
        context.req?.headers
      );
      if (isAppIimjobs) {
        const eligibleData = await userService.cohortEligible(
          'free-pro-cohort',
          {
            headers: {
              cookie: context.req?.headers?.cookie,
              ...ssrHeaders,
            },
          }
        );
        if (eligibleData?.eligible) {
          isCohortEligible = true;
        }
      }

      if (cookies?.[COOKIE_LOGIN_AUTH]) {
        headers = {
          cookie: context.req?.headers?.cookie,
          ...ssrHeaders,
        };

        const proRes = await userService.checkPro('checkpro', {headers});
        console.log('🚀 ~ file: [jobslug].tsx:150 ~ store=> ~ proRes:', proRes);

        if (proRes?.type && ['refresh', 'logout'].includes(proRes.type)) {
          if (proRes?.type === 'refresh' && proRes?.token) {
            const {ssrLastActCookie, ssrTokenCookie} = setSSRCookies(
              proRes.token
            );

            context.res?.setHeader('Set-Cookie', [
              ssrTokenCookie,
              ssrLastActCookie,
            ]);
          }
          if (proRes?.type === 'logout') {
            const cookiesTodelete = getCookiesToDelete();
            context.res?.setHeader('Set-Cookie', cookiesTodelete);
          }
        }

        store.dispatch({type: 'Auth/set_pro', payload: proRes?.pro ?? false});
      }
    }

    const seoConfig = await loadSEO(
      SAVEDJOBS,
      {
        title: 'Applied Jobs',
        description: '',
      },
      slugUrl,
      true
    );
    seoConfig.title = 'Applied Jobs';

    return {
      props: {
        seoConfig,
        metaData,
        isCohortEligible,
      },
    };
  }
);

export default AppliedJobs;
